<template>
  <section id="userIndex">
    <v-row justify="space-between" align="center">
      <v-col cols="7" md="auto" class="flex-grow-1 d-flex align-center">
        <v-btn
          @click="backAction"
          rounded
          class="rounded-xl mr-3"
          small
          plain
          min-width="0"
          :dark="isDark"
        >
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <h2
          :class="[
            isMobile ? 'h5' : 'h4',
            isMobile && isDark ? 'white--text' : 'primary--text',
          ]"
          style="word-break: normal"
        >
          {{ `${agentName} - ${evaluationName}` }}
        </h2>
      </v-col>
      <v-col cols="auto">
        <v-menu offset-y>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              fab
              small
              color="info"
              v-bind="attrs"
              v-on="on"
              :loading="isDownloadingReport"
            >
              <v-icon> mdi-tray-arrow-down </v-icon>
              <!-- Descargar -->
            </v-btn>
          </template>
          <v-list>
            <v-list-item @click="downloadReport(item)">
              <v-list-item-title> Reporte resumido </v-list-item-title>
            </v-list-item>
            <v-list-item @click="downloadDetailReport(item)">
              <v-list-item-title> Reporte detallado </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-col>
      <v-col cols="auto" v-if="user !== 'agent'">
        <v-switch
          class="ml-auto"
          v-model="activeGraph"
          inset
          label="Ver gráficas"
        ></v-switch>
      </v-col>
    </v-row>
    <v-card class="shadow-1">
      <v-card flat>
        <!-- Tabla -->
        <v-container v-show="!activeGraph">
          <v-skeleton-loader v-if="isLoading" type="table"> </v-skeleton-loader>

          <v-row v-else dense justify="end">
            <v-col cols="12">
              <vuescroll
                :ops="{
                  bar: {
                    background: '#bacfcec0',
                    keepShow: true,
                  },
                }"
              >
                <v-data-table
                  :headers="tableHeaders"
                  :items="reportsList"
                  class="elevation-0"
                  :items-per-page="15"
                  :options.sync="tableOptions"
                  :server-items-length="totalItems"
                  :loading="isLoadingTable"
                  :footer-props="{
                    itemsPerPageOptions: [0, 15],
                  }"
                >
                  <template v-slot:[`item.date`]="{ item }">
                    <span class="d-inline pr-2">
                      {{ normalizeDate(item.date) }}
                    </span>
                  </template>
                  <template v-slot:[`item.user`]="">
                    <span class="d-inline pr-2">
                      {{ itemToShow.name }}
                    </span>
                  </template>
                  <template v-slot:[`item.extension`]="">
                    <span class="d-inline pr-2">
                      {{ itemToShow.extension }}
                    </span>
                  </template>
                  <template v-slot:[`item.action`]="{ item }">
                    <v-hover v-slot:default="{ hover }">
                      <v-btn
                        text
                        icon
                        :color="hover ? 'accent' : 'blue-grey'"
                        @click.stop="viewResults(item)"
                      >
                        <v-icon>mdi-eye</v-icon>
                      </v-btn>
                    </v-hover>
                    <v-hover v-if="user !== 'agent'" v-slot:default="{ hover }">
                      <v-btn
                        text
                        icon
                        :color="hover ? 'accent' : 'blue-grey'"
                        @click.stop="openEditForm(item)"
                      >
                        <v-icon>mdi-pencil</v-icon>
                      </v-btn>
                    </v-hover>
                    <v-hover v-if="user === 'agent'" v-slot:default="{ hover }">
                      <v-btn
                        text
                        icon
                        :color="hover ? 'accent' : 'blue-grey'"
                        @click.stop="openAgentComment(item)"
                      >
                        <v-icon>mdi-comment</v-icon>
                      </v-btn>
                    </v-hover>
                  </template>
                </v-data-table>
              </vuescroll>
            </v-col>
          </v-row>
        </v-container>
        <!-- Agent comment -->
        <v-dialog v-model="agentCommentDialog" max-width="550">
          <ValidationObserver ref="obs" v-slot="{}">
            <v-card>
              <v-card-title>Publicar Comentario </v-card-title>
              <v-card-text>
                <ValidationProvider
                  name="comment"
                  rules="required"
                  v-slot="{ errors }"
                >
                  <v-text-field
                    required
                    filled
                    rounded
                    class="rounded-small"
                    dense
                    label="Comentarios"
                    name="comment"
                    prepend-inner-icon="mdi-comment"
                    color="info"
                    v-model="agentCommentInput"
                    :error-messages="errors"
                  ></v-text-field>
                </ValidationProvider>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-hover v-slot:default="{ hover }">
                  <v-btn
                    text
                    rounded
                    class="mr-1"
                    :color="hover ? 'error' : 'grey'"
                    @click="closeAgentComment()"
                    >Cancelar</v-btn
                  >
                </v-hover>

                <v-hover v-slot:default="{ hover }">
                  <v-btn
                    rounded
                    :loading="isLoading"
                    :disabled="isLoading"
                    color="success"
                    :class="hover ? 'shadow-2' : 'shadow-4'"
                    class="px-8"
                    @click="sendAgentComment()"
                  >
                    Publicar
                  </v-btn>
                </v-hover>
              </v-card-actions>
            </v-card>
          </ValidationObserver>
        </v-dialog>

        <!-- Results -->
        <results-dialog
          :isOpen="resultsDialog"
          :itemToResults="itemToResults"
          :recordingUrl="recordingUrl"
          @dialog:close="closeResultsDialog"
        ></results-dialog>

        <v-dialog
          :value="isOpenFormDialog"
          @click:outside="closeFormDialog"
          scrollable
          max-width="950"
        >
          <test-form
            v-if="isOpenFormDialog"
            viewMode="dialog"
            @processFormDialog:close="closeFormDialog"
            :dataToForm="itemToResults"
            mode="edit"
          />
        </v-dialog>
        <!-- Gráficas -->
        <v-container v-if="activeGraph" fluid>
          <v-row align="center" justify="center">
            <v-col v-if="render" cols="12" md="6">
              <v-card outlined>
                <v-card-text>
                  <apexchart
                    ref="chart7"
                    type="bar"
                    :options="optionsBar"
                    :series="barSeries"
                  ></apexchart>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col v-else cols="12" md="6">
              <v-skeleton-loader
                transition="scale-transition"
                class="mx-auto"
                type="card"
              >
              </v-skeleton-loader>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-card>
  </section>
</template>

<script>
import axios from "axios";
import moment from "moment";
import vuescroll from "vuescroll";
import { PbxHttpClient } from "@/services/httpclient";
import TestForm from "@/components/Tests/TestForm.vue";
import ResultsDialog from "./ResultsDialog.vue";

export default {
  components: { vuescroll, ResultsDialog, TestForm },
  props: {
    idEvaluation: {
      type: String,
      default: null,
    },
    itemToShow: {
      type: Object,
      default: null,
    },
    user: {
      type: String,
      default: "default",
    },
  },
  data() {
    return {
      reportsList: [],
      tableHeaders: [
        {
          text: "Fecha",
          value: "date",
          width: "16%",
        },
        {
          text: "Usuario",
          value: "user",
          width: "10%",
        },
        {
          text: "Extensión",
          value: "extension",
          width: "10%",
        },
        {
          text: "Numero",
          value: "number",
          width: "10%",
        },
        {
          text: "puntaje obtenido",
          value: "value",
          width: "15%",
        },
        {
          text: "Observación",
          value: "observations",
          width: "17%",
        },
        {
          text: "Comentarios del agente",
          value: "agent_comment",
          width: "17%",
        },

        {
          text: "Acciones",
          value: "action",
          width: "15%",
        },
      ],
      itemToResults: {},
      recordingUrl: "",
      resultsDialog: false,
      isOpenFormDialog: false,
      agentCommentDialog: false,
      agentCommentInput: "",
      commentTestId: null,
      render: false,
      isLoading: true,
      isLoadingTable: false,
      isDownloadingReport: false,
      activeGraph: false,
      evaluationToShow: null,
      mainSection: true,
      page: 1,
      totalItems: 0,
      tableOptions: {},
      reportApi: {
        default: `/quality/report/evaluation/detaill/${this.idEvaluation}/${this.itemToShow.id}`,
        agent: `/report/agent/test_agent/${this.idEvaluation}`,
      },
      optionsDonut: {
        responsive: [
          {
            breakpoint: 480,
            options: {
              chart: {
                width: "100%",
              },
            },
          },
        ],
        width: "100%",
        legend: {
          position: "bottom",
        },
        dataLabels: { enabled: false },
        labels: [],
      },
      optionsBar: {
        xaxis: {
          categories: [],
        },
        fill: {
          colors: ["#6D71F9"],
        },
      },
    };
  },
  created() {
    this.getReports();
    this.getRecordingUrl();
    // this.getTests();
  },

  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mobile;
    },
    isDark() {
      return this.$vuetify.theme.dark;
    },
    evaluationName() {
      return this.itemToShow.test[0].evaluation.name;
    },
    agentName() {
      return this.itemToShow.name;
    },
    barSeries() {
      return [{ name: "Valor", data: this.reportsList.map((el) => el.value) }];
    },
    Tests() {
      const test = this.$store.getters["tests/GET_ALL_TESTS"];
      return test.length > 0
        ? test.filter(
            (t) =>
              t.evaluation_id === Number(this.idEvaluation) &&
              t.user_id === this.itemToShow.id
          )
        : [];
    },
  },
  watch: {
    page() {
      this.getReports();
    },
    tableOptions: {
      handler() {
        this.getReports();
      },
      deep: true,
    },
  },

  methods: {
    async viewResults(item) {
      await this.getTest(item.id);
      this.resultsDialog = true;
    },
    async openEditForm(item) {
      await this.getTest(item.id);
      this.isOpenFormDialog = true;
    },
    closeFormDialog(reloadList = false) {
      this.itemToResults = {};
      this.isOpenFormDialog = false;
      if (reloadList) this.getReports();
    },
    normalizeDate(date) {
      return moment(date).format("YYYY-MM-DD");
    },
    getAverage(item) {
      return item.cantTest > 0
        ? item.test.reduce(
            (prev, next) => (next.value ? prev + next.sumTest : prev),
            0
          ) / item.cantTest
        : "0";
    },
    backAction() {
      this.$emit("mainSection");
    },
    async getReports() {
      this.isLoadingTable = true;
      const page = this.tableOptions?.page || 1;
      const itemPerPage = this.tableOptions.itemsPerPage || 15;
      try {
        const { data } = await PbxHttpClient.get(
          `${this.reportApi[this.user]}`,
          {
            params: {
              page,
              approved_status: this.$route.params.status,
              itemPerPage,
            },
          }
        );

        this.reportsList = data.data;
        this.totalItems = data.total;
        // this.optionsDonut.labels = res.data.data.map((el) => el.date);
        this.optionsBar.xaxis.categories = data.data.map((el) => el.date);
      } catch (error) {
        // console.error(error);
        this.$snotify.error("Hubo un error en la consulta.", "Error");
      }
      this.render = true;
      this.isLoadingTable = false;
      this.isLoading = false;
    },
    async getRecordingUrl() {
      this.isLoading = true;
      const accessToken = localStorage.getItem("token");
      try {
        const { data } = await axios.get(
          `${process.env.VUE_APP_ROOTAPI}/url_client`,
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        this.recordingUrl = data.url;
        // console.log("URL", data);
      } catch (err) {
        console.log(err);
      }
      this.isLoading = false;
    },

    //Downloads
    async downloadReport() {
      this.isDownloadingReport = true;
      try {
        const res = await PbxHttpClient.get(
          `/quality/export/evaluation/detaill/${this.idEvaluation}/${this.itemToShow.id}`,
          {
            responseType: "blob",
          }
        );
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Reporte resumido de ${this.agentName} - ${this.evaluationName}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
      } catch (err) {
        this.$snotify.error("Hubo un error descargando el reporte.", "Error");
      }
      this.isDownloadingReport = false;
    },
    async downloadDetailReport() {
      this.isDownloadingReport = true;
      try {
        const res = await PbxHttpClient.get(
          `/quality/export/evaluation/answer/${this.idEvaluation}/${this.itemToShow.id}}`,
          {
            responseType: "blob",
          }
        );
        const url = window.URL.createObjectURL(new Blob([res.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `Reporte detallado de ${this.agentName} - ${this.evaluationName}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
      } catch (err) {
        this.$snotify.error("Hubo un error descargando el reporte.", "Error");
      }
      this.isDownloadingReport = false;
    },
    //Store
    async getTest(id) {
      try {
        const { data } = await PbxHttpClient.get(`/quality/test/${id}`);
        this.itemToResults = data.data;
      } catch (err) {}
    },
    async sendAgentComment() {
      const accessToken = localStorage.getItem("token");
      try {
        const { data } = await axios.put(
          `${process.env.VUE_APP_ROOTAPI}/report/agent/update_comment/${this.commentTestId}`,
          { comment: this.agentCommentInput },
          {
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );
        this.reportsList = this.reportsList.map((el) =>
          el.id === this.commentTestId ? data.test : el
        );
        this.$snotify.success("Comentario agregado con éxito.", "¡Hecho!");
      } catch (error) {
        // console.error(error);
        this.$snotify.error("Hubo un error publicando el comentario.", "Error");
      }
      this.closeAgentComment();
    },
    openAgentComment(item) {
      this.commentTestId = item.id;
      this.agentCommentInput = item.agent_comment || "";
      this.agentCommentDialog = true;
    },
    closeAgentComment() {
      this.agentCommentDialog = false;
      this.commentTestId = null;
    },
    closeResultsDialog() {
      this.itemToResults = {};
      this.resultsDialog = false;
    },
  },
};
</script>

<style>
.shadow-1 {
  -webkit-box-shadow: 0 8px 16px 0 rgba(40, 40, 90, 0.09),
    0 3px 6px 0 rgba(0, 0, 0, 0.065);
  box-shadow: 0 8px 16px 0 rgba(40, 40, 90, 0.09),
    0 3px 6px 0 rgba(0, 0, 0, 0.065);
}
</style>
